import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { withStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';

import { CurrencyFormat } from '../../../common/numberFormatCustom.component';
import { Fragment } from 'react';
import { formatDateWithSlashes } from '../../../utilities/dateFormatter';
import ReadOnlyText from '../../../common/readOnlyText.component';
import { userHasPermission } from 'utilities/authUtils';

const styles = (theme) => ({
  cardsContainer: {
    columnCount: 3,
    columnGap: theme.spacing(2),
    [theme.breakpoints.down('md')] : {
      columnCount: 2,
    },
    [theme.breakpoints.down('xs')] : {
      columnCount: 1,
      textAlign: 'center',
    },
  },
  cardContainer: {
    '-webkit-column-break-inside': 'avoid',
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(1),
  },
  internationalCardContainer: {
    '-webkit-column-break-inside': 'avoid',
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    width: '100%'
  },
  serviceSubHeader: {
    textAlign: 'center'
  },
  card: {
    overflow: 'visible',
  },
  cardHeader: {
    display: 'flex',
    position: 'relative',
    width: '100%',
    height: '70px',
  },
  cardHeaderIntl: {
    display: 'flex',
    position: 'relative',
    width: '100%',
    height: '70px',
    marginBottom: '20px',
  },
  cardIcon: {
    height: '90px',
    width: '90px',
    position: 'absolute',
    top: '-45%',
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'center',
    '& > svg': {
      height: '75px',
      width: '75px',
    },
  },
  cardHeaderText: {
    marginTop: 'auto',
    marginBottom: 'auto',
    marginLeft: '7rem',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: 'rgba(0, 0, 0, 0.54)',
  },
  bullet: {
    display: 'inline-block',
    margin: '0 2px',
    transform: 'scale(0.8)',
  },
  subtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
  pos: {
    marginBottom: 12,
    color: theme.palette.text.secondary,
  },
  hhgTabIndicator: {
    backgroundColor: '#43AAA2',
  },
  hhgTabRoot: {
    minWidth: 'unset',
    opacity: 0.3,
  },
  hhgTabRootSelected: {
    // color: theme.palette.primary.light,
    color: '#43AAA2',
    opacity: 1,
  },
});

const boolFormat = (val) => {
  if (val === 'N') return 'No';
  else if (val === 'Y') return 'Yes';
  else return val;
}

const INTERNATIONAL_FIELDS_OVERVIEW_ONE = [
  [
    { key: 'assignmentStatus', label: 'Assignment Status', size: 9 },
    { key: 'assignmentType', label: 'Assignment Type', size: 8 },
    { key: 'assignmentCountry', label: 'Assignment Country', size: 9 },
    { key: 'assignmentLength', label: 'Assignment Length', size: 10 },
    { key: 'estStartDate', label: 'Estimated Start Date', size: 10, formatter: formatDateWithSlashes },
    { key: 'startDate', label: 'Start Date', formatter: formatDateWithSlashes },
    { key: 'estReturnDate', label: 'Estimated Return Date', size: 12, formatter: formatDateWithSlashes },
    { key: 'actualReturnDate', label: 'Return Date', formatter: formatDateWithSlashes },
    { key: 'extensionDate', label: 'Extension Date', size: 8, formatter: formatDateWithSlashes },
    { key: 'completedDate', label: 'Completed Date', size: 8, formatter: formatDateWithSlashes },
    { key: 'cancelledDate', label: 'Cancelled Date', size: 8, formatter: formatDateWithSlashes },
  ]
];

const INTERNATIONAL_FIELDS_OVERVIEW_TWO = [
  [
    { key: 'homeCountry', label: 'Citizenship' },
    { key: 'socSecCertExpires', label: 'Certificate Expires', formatter: formatDateWithSlashes },
    { key: 'workPermit', label: 'Work Permit', formatter: boolFormat },
    { key: 'workPermitAcquired', label: 'Work Permit Acquired', size: 9, formatter: formatDateWithSlashes },
    { key: 'residencyPermit', label: 'Residency Permit', formatter: boolFormat },
    { key: 'residencyPermitAcquired', label: 'Res Permit Acquired', size: 10, formatter: formatDateWithSlashes },
    { key: 'immunizationRequired', label: 'Immunization Required', size: 12, formatter: boolFormat },
    { key: 'immunizationFiled', label: 'Immunization Filed', size: 10, formatter: boolFormat },
    { key: 'immunizationFiledDate', label: 'Immunization Filed Date', size: 10, formatter: formatDateWithSlashes },
    { key: 'insurance', label: 'Insurance', formatter: boolFormat },
  ]
];

const INTERNATIONAL_FIELDS_COSTS = [
  [
    { key: 'costEstimateRequested', label: 'Estimate Requested', size: 9, formatter: formatDateWithSlashes },
    { key: 'costEstimateCompleted', label: 'Estimate Completed', size: 9, formatter: formatDateWithSlashes },
    { key: 'costEstimateRevRequested', label: 'Estimate Rev Requested', size: 9, formatter: formatDateWithSlashes },
    { key: 'costEstimateRevCompleted', label: 'Estimate Rev Completed', size: 9, formatter: formatDateWithSlashes },
    { key: 'preAssignmentHRConsult', label: 'Pre Assign HR Consult', size: 9, formatter: formatDateWithSlashes },
  ]
];

const INTERNATIONAL_FIELDS_VISA = [
  [
    { key: 'currentVisaStatus', label: 'Current Visa Status' },
    { key: 'visaExpiration', label: 'Visa Expiration', formatter: formatDateWithSlashes },
    { key: 'visaMaxDate', label: 'Visa Max Date', formatter: formatDateWithSlashes },
    { key: 'visaIssuedBy', label: 'Visa Issued By' },
    { key: 'visaStatus', label: 'Visa Status' },
    { key: 'passportReceivedDate', label: 'Passport Received Date', formatter: formatDateWithSlashes },
    { key: 'passportIssuedBy', label: 'Passport Issued By' },
    { key: 'passportExpires', label: 'Passport Expires', formatter: formatDateWithSlashes },
  ]
];

const HHG_FIELDS = [
  [
    { key: 'estPackDate', label: 'Estimated Pack Date', formatter: formatDateWithSlashes },
    { key: 'actualPackDate', label: 'Actual Pack Date', formatter: formatDateWithSlashes },
  ],
  [
    { key: 'estLoadDate', label: 'Estimated Load Date', formatter: formatDateWithSlashes },
    { key: 'actualLoadDate', label: 'Actual Load Date', formatter: formatDateWithSlashes },
  ],
  [
    { key: 'estStorageDate', label: 'Estimated Storage Date', formatter: formatDateWithSlashes },
    { key: 'actualStorageDate', label: 'Actual Storage Date', formatter: formatDateWithSlashes },
  ],
  [
    { key: 'actualDateOutOfStorage', label: 'Actual Date Out of Storage', formatter: formatDateWithSlashes },
  ],
  [
    { key: 'estDeliveryDate', label: 'Estimated Delivery Date', formatter: formatDateWithSlashes },
    { key: 'actualDeliveryDate', label: 'Actual Delivery Date', formatter: formatDateWithSlashes },
  ],
];

const HHG_SEA_AIR_FIELDS = [
  [
    { key: 'estPackDate', label: 'Estimated Start Date', formatter: formatDateWithSlashes },
    { key: 'actualPackDate', label: 'Actual Start Date', formatter: formatDateWithSlashes },
  ],
    [
    { key: 'estPackEndDate', label: 'Estimated End Date', formatter: formatDateWithSlashes },
    { key: 'actualPackEndDate', label: 'Actual End Date', formatter: formatDateWithSlashes },
  ],
];

const HHG_FIELD_LIST = HHG_FIELDS.reduce((fields, row) => {
  const keys = row.map((field) => field.key);
  return fields.concat(keys);
}, []);

const hasHhgData = (data) => {
  if (!data) {
    return false;
  }
  return !!HHG_FIELD_LIST.find((key) => !!data[key]);
};

function HhgTab(props) {
  const { data, type } = props;
  return (
    <div>
    { (type === 'Land') &&
      HHG_FIELDS.map((row, i) => {
      return (
        <Grid container key={i}>
          {row.map((field) => {
            const { formatter, key, label, multiline, size } = field;
            let value = data[key];
            const columnSize = size ? size : 6;

            if (value || typeof formatter === 'function') {
              value = formatter(value);
            }

            return (
              <Grid item xs={columnSize} key={key}>
                <ReadOnlyText
                  label={label}
                  textColor="#ba8a00"
                  value={value || ' '}
                  multiline={multiline}
                />
              </Grid>
            );
          })}
        </Grid>
      );
    })
    }
    { (type !== 'Land') &&
    HHG_SEA_AIR_FIELDS.map((row, i) => {
      return (
        <Grid container key={i}>
          {row.map((field) => {
            const { formatter, key, label, multiline, size } = field;
            let value = data[key];
            const columnSize = size ? size : 6;

            if (value || typeof formatter === 'function') {
              value = formatter(value);
            }

            return (
              <Grid item xs={columnSize} key={key}>
                <ReadOnlyText
                  label={label}
                  textColor="#ba8a00"
                  value={value || ' '}
                  multiline={multiline}
                />
              </Grid>
            );
          })}
        </Grid>
      );
    })
    }
    </div>
    
    
  );
}

class ServicesCards extends Component {

  state = { hhgSelectedTab: 0 };

  parseStringToArray(string) {
    if (string && string.length > 0) {
      return string.split(';').map((value) => value.trim()).filter((value) => value.length > 0);
    }
    return [];
  }

  getCustomFieldDependencyValue(dependentField, transfereeInfo, customFields) {
    if (dependentField === 'policyId') {
      return transfereeInfo ? (transfereeInfo.policyId ?? transfereeInfo.PolicyId ?? null) : null;
    }

    if (dependentField === 'divisionId') {
      return transfereeInfo ? (transfereeInfo.division ?? null) : null;
    }

    const dependentCustomField = Array.isArray(customFields)
      ? customFields.find((fieldValue) => fieldValue.customFieldCommonId === dependentField)
      : null;

    return dependentCustomField ? dependentCustomField.fieldValue : null;
  }

  shouldDisplayCustomField(field, transfereeInfo, customFields, divisions, policies) {

    if (!field || !Array.isArray(field.customFieldDependencies) || field.customFieldDependencies.length === 0) {
      return true;
    }

    const evaluateDependency = (dependency) => {
      let dependentField = dependency.fieldDependentOnId;
      let dependentOnValue = dependency.fieldDependentOnValueId;

      if(dependentField === 'Division') {
        dependentField = 'divisionId';
        const dependentDivisions = this.parseStringToArray(dependentOnValue);
        dependentOnValue = [];
        dependentDivisions.forEach((division) => {
          if (divisions && divisions.length > 0) {
            const matchedDivision = divisions.find((div) => div.divisionName === division);
            if (matchedDivision) {
              dependentOnValue.push(matchedDivision.divisionName);
            }
          }
        });
      }
      else if(dependentField === 'Relocation Policy') {
        dependentField = 'policyId';
        const dependentPolicies = this.parseStringToArray(dependentOnValue);
        dependentOnValue = [];
        dependentPolicies.forEach((policy) => {
          if (policies && policies.length > 0) {
            const matchedPolicy = policies.find((policyItem) => policyItem && policyItem.policy && policyItem.policy.policyDescription === policy);
            if (matchedPolicy) {
              dependentOnValue.push(matchedPolicy.policy.relocationPolicyId);
            }
          }
        });
      }
      else {
        const matchedCustomField = customFields && customFields.length > 0
          ? customFields.find((fieldItem) => fieldItem.fieldLabel === dependentField)
          : null;
        dependentField = matchedCustomField ? matchedCustomField.customFieldCommonId : null;
      }

      const dependentFieldValue = this.getCustomFieldDependencyValue(dependentField, transfereeInfo, customFields);
      const candidateValues = Array.isArray(dependentOnValue) ? dependentOnValue : [dependentOnValue];

      return candidateValues.some((value) => {
        switch (dependency.fieldDependentOnOperationId) {
        case '98ba1156-a956-45db-9d68-10f2e345b7f9':
          return dependentFieldValue === value;
        case '6b8ec837-01c9-4773-ad3c-0e6c41263678':
          return dependentFieldValue !== value;
        case '9b12aa29-1027-40a0-9843-8d861d688249':
          return dependentFieldValue != null && dependentFieldValue !== '';
        case 'd73311f5-41bc-4e0e-8048-92b0722c5bae':
          return dependentFieldValue == null || dependentFieldValue === '';
        case 'e78add1f-2d1c-4241-b15b-2a7dcf12657d':
          return dependentFieldValue > value;
        case '01a99433-8b24-4ac8-a004-7e9c8e17c3a0':
          return dependentFieldValue < value;
        default:
          return false;
        }
      });
    };

    const dependenciesByFieldId = field.customFieldDependencies.reduce((grouped, dependency) => {
      const key = dependency && dependency.fieldDependentOnId ? dependency.fieldDependentOnId : '__unknown_dependency__';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(dependency);
      return grouped;
    }, {});

    return Object.keys(dependenciesByFieldId).every((fieldDependentOnId) => {
      const groupedDependencies = dependenciesByFieldId[fieldDependentOnId] || [];
      return groupedDependencies.some((dependency) => evaluateDependency(dependency));
    });
  }

  formatCustomFieldValue(field) {
    const value = field ? field.fieldValue : null;

    if (field && field.fieldTypeId === '1235AE4C-BA8F-4BA6-91C7-2AF61524325A') {
      return value ? formatDateWithSlashes(value) : ' ';
    }

    if (field && field.fieldTypeId === '725cc7d4-507f-4305-ad68-4d412a73ee70') {
      if (value === true) return 'Yes';
      if (value === false) return 'No';
      return boolFormat(value) || ' ';
    }

    return value ? value : ' ';
  }

  renderHomeSaleCard() {
    const {
      classes,
      transfereeInfo,
      homesaleSummary
    } = this.props;

    if (homesaleSummary === null) {
      return null;
    }

    return (
      <div className={classes.cardContainer}>
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.cardHeader}>
              <Paper className={classes.cardIcon} style={{ background: 'linear-gradient(45deg, #224d88, #4182ca)' }}>
                <FontAwesomeIcon icon="home" color="white" size="4x" />
              </Paper>
              <div className={classes.cardHeaderText}>HOME SALE</div>
            </div>
            {transfereeInfo.departureAddressLine1 &&
              <div>
                <Typography className={classes.subtitle}>{transfereeInfo.departureAddressLine1}</Typography>
                <Typography className={classes.subtitle}>{transfereeInfo.departureAddressLine2}</Typography>
                <Typography className={classes.subtitle}>{`${transfereeInfo.departureCity  }, ${  transfereeInfo.departureState  } ${  transfereeInfo.departureZip}`}</Typography>
              </div>
            }
            <Grid container>
              <Grid item xs={12}>
                <ReadOnlyText
                  label="BMA Average"
                  name="bmaAverage"
                  textColor="#4182ca"
                  value={(homesaleSummary.bmaAverage) ? homesaleSummary.bmaAverage : ' '}
                  inputComponent={CurrencyFormat}
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={6}>
                <ReadOnlyText
                  label="Listing Start Date"
                  name="listBegins"
                  textColor="#4182ca"
                  value={(homesaleSummary.listBegins) ? formatDateWithSlashes(homesaleSummary.listBegins) : ' '}
                />
              </Grid>
              <Grid item xs={6}>
                <ReadOnlyText
                  label="Listing End Date"
                  name="listEnds"
                  textColor="#4182ca"
                  value={(homesaleSummary.listEnds) ? formatDateWithSlashes(homesaleSummary.listEnds) : ' '}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <ReadOnlyText
                label="Current List Price"
                name="curListPrice"
                textColor="#4182ca"
                value={(homesaleSummary.listPrice) ? homesaleSummary.listPrice : ' '}
                inputComponent={CurrencyFormat}
              />
            </Grid>
            <Grid container>
              <Grid item xs={6}>
                <ReadOnlyText
                  label="Contract Date"
                  name="contractDate"
                  textColor="#4182ca"
                  value={(homesaleSummary.OfferAcceptDate) ? formatDateWithSlashes(homesaleSummary.OfferAcceptDate) : ' '}
                />
              </Grid>
              <Grid item xs={6}>
                <ReadOnlyText
                  label="Contract Price"
                  name="contractPrice"
                  textColor="#4182ca"
                  value={(homesaleSummary.offerAcceptAmt) ? homesaleSummary.offerAcceptAmt : ' '}
                  inputComponent={CurrencyFormat}
                />
              </Grid>
            </Grid>
            {/* <Grid container>
              <Grid item xs={6}>
                <ReadOnlyText
                  label="Est. Close Date"
                  name="estCloseDate"
                  textColor="#4182ca"
                  value={(SchedBuyoutDate) ? formatDateWithSlashes(SchedBuyoutDate) : ' '}
                />
              </Grid>
              <Grid item xs={6}>
                <ReadOnlyText
                  label="Actual Close Date"
                  name="actCloseDate"
                  textColor="#4182ca"
                  value={(ActualBuyoutCloseDate) ? formatDateWithSlashes(ActualBuyoutCloseDate) : ' '}
                />
              </Grid> 
            </Grid>*/}
            <Grid container>
              <Grid item xs={12}>
                <ReadOnlyText
                  label="Expiration Date"
                  name="expirationDate"
                  textColor="#4182ca"
                  value={(homesaleSummary.offerExpires) ? formatDateWithSlashes(homesaleSummary.offerExpires) : ' '}
                />
              </Grid>
            </Grid>

          </CardContent>
        </Card>
      </div>
    );
  }

  // renderHouseholdGoodsCard_old() {
  //   const {
  //     classes,
  //     transfereeInfo,
  //     hhgSummary
  //   } = this.props;

  //   if (ServiceStatuses.HHGServiceStatus === null) {
  //     return null;
  //   }

  //   return (
  //     <div className={classes.cardContainer}>
  //       <Card className={classes.card}>
  //         <CardContent>
  //           <div className={classes.cardHeader}>
  //             <Paper className={classes.cardIcon} style={{ background: 'linear-gradient(45deg, #ba8a00, #f0b11d)' }}>
  //               <FontAwesomeIcon icon="archive" color="white" size="4x" />
  //             </Paper>
  //             <div className={classes.cardHeaderText}>HOUSEHOLD GOODS</div>
  //           </div>
  //           <Grid container>
  //             <Grid item xs={6}>
  //               <ReadOnlyText
  //                 label="Estimated Pack Date"
  //                 name="estPackDate"
  //                 textColor="#ba8a00"
  //                 value={(hhgSummary.packEstEnd) ? formatDateWithSlashes(hhgSummary.packEstEnd) : ' '}
  //               />
  //             </Grid>
  //             <Grid item xs={6}>
  //               <ReadOnlyText
  //                 label="Actual Pack Date"
  //                 name="actPackDate"
  //                 textColor="#ba8a00"
  //                 value={(hhgSummary.packActEnd) ? formatDateWithSlashes(hhgSummary.packActEnd) : ' '}
  //               />
  //             </Grid>
  //           </Grid>
  //           <Grid container>
  //             <Grid item xs={6}>
  //               <ReadOnlyText
  //                 label="Estimated Load Date"
  //                 name="estLoadDate"
  //                 textColor="#ba8a00"
  //                 value={(hhgSummary.loadEstEnd) ? formatDateWithSlashes(hhgSummary.loadEstEnd) : ' '}
  //               />
  //             </Grid>
  //             <Grid item xs={6}>
  //               <ReadOnlyText
  //                 label="Actual Load Date"
  //                 name="actLoadDate"
  //                 textColor="#ba8a00"
  //                 value={(hhgSummary.loadActEnd) ? formatDateWithSlashes(hhgSummary.loadActEnd) : ' '}
  //               />
  //             </Grid>
  //           </Grid>
  //           <Grid container>
  //             <Grid item xs={6}>
  //               <ReadOnlyText
  //                 label="Est. Storage Date"
  //                 name="estStoreDate"
  //                 textColor="#ba8a00"
  //                 value={(hhgSummary.storageEstEnd) ? formatDateWithSlashes(hhgSummary.storageEstEnd) : ' '}
  //               />
  //             </Grid>
  //             <Grid item xs={6}>
  //               <ReadOnlyText
  //                 label="Actual Storage Date"
  //                 name="actStoreDate"
  //                 textColor="#ba8a00"
  //                 value={(hhgSummary.storageActStart) ? formatDateWithSlashes(hhgSummary.storageActStart) : ' '}
  //               />
  //             </Grid>
  //           </Grid>
  //           <Grid container>
  //             <Grid item xs={12}>
  //               <ReadOnlyText
  //                 label="Actual Date Out of Storage"
  //                 name="actStorageEnd"
  //                 textColor="#ba8a00"
  //                 value={(hhgSummary.storageActEnd) ? formatDateWithSlashes(hhgSummary.storageActEnd) : ' '}
  //               />
  //             </Grid>
  //           </Grid>
  //           <Grid container>
  //             <Grid item xs={6}>
  //               <ReadOnlyText
  //                 label="Est. Delivery Date"
  //                 name="actDeliveryDate"
  //                 textColor="#ba8a00"
  //                 value={(hhgSummary.deliveryEstEnd) ? formatDateWithSlashes(hhgSummary.deliveryEstEnd) : ' '}
  //               />
  //             </Grid>
  //             <Grid item xs={6}>
  //               <ReadOnlyText
  //                 label="Actual Delivery Date"
  //                 name="estDeliveryDate"
  //                 textColor="#ba8a00"
  //                 value={(hhgSummary.deliveryActEnd) ? formatDateWithSlashes(hhgSummary.deliveryActEnd) : ' '}
  //               />
  //             </Grid>
  //           </Grid>
  //         </CardContent>
  //       </Card>
  //     </div>
  //   );
  // }

  handleChange = (event, value) => {
    this.setState({ hhgSelectedTab: value });
  };

  renderHouseholdGoodsCard() {
    const {
      classes,
      hhgSummary,
    } = this.props;

    if (hhgSummary === null || hhgSummary.length === 0) {
      return null;
    }

    const { hhgSelectedTab } = this.state;

    const landData = hhgSummary.find((item) => item.hhgType === 'Land');
    const airData = hhgSummary.find((item) => item.hhgType === 'Air');
    const seaData = hhgSummary.find((item) => item.hhgType === 'Sea');

    const hasLand = hasHhgData(landData);
    const hasAir = hasHhgData(airData);
    const hasSea = hasHhgData(seaData);

    if (!hasLand && !hasAir && !hasSea) {
      return null;
    }

    const selectedTab = hhgSelectedTab ? hhgSelectedTab :
      hasLand ? 'land' :
        hasAir ? 'air' :
          hasSea ? 'sea' : null;

    return (
      <div className={classes.cardContainer}>
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.cardHeader}>
              <Paper className={classes.cardIcon} style={{ background: 'linear-gradient(45deg, #ba8a00, #f0b11d)' }}>
                <FontAwesomeIcon icon="box-open" color="white" size="4x" />
              </Paper>
              <div className={classes.cardHeaderText}>HOUSEHOLD GOODS</div>
            </div>
            <Tabs
              value={selectedTab}
              onChange={this.handleChange}
              variant="fullWidth"
              classes={{ indicator: classes.hhgTabIndicator }}
            >
              {hasLand &&
                <Tab
                  classes={{ root: classes.hhgTabRoot, selected: classes.hhgTabRootSelected }}
                  icon={<FontAwesomeIcon icon="truck-moving" size="2x" />}
                  label="Land"
                  value="land"
                />
              }
              {hasAir &&
                <Tab
                  classes={{ root: classes.hhgTabRoot, selected: classes.hhgTabRootSelected }}
                  icon={<FontAwesomeIcon icon="plane-departure" size="2x" />}
                  label="Air"
                  value="air"
                />
              }
              {hasSea &&
                <Tab
                  classes={{ root: classes.hhgTabRoot, selected: classes.hhgTabRootSelected }}
                  icon={<FontAwesomeIcon icon="ship"  size="2x" />}
                  label="Sea"
                  value="sea"
                />
              }
            </Tabs>
            {selectedTab === 'land' && <HhgTab data={landData} type={'Land'}/>}
            {selectedTab === 'air' && <HhgTab data={airData} type={'Air'} />}
            {selectedTab === 'sea' && <HhgTab data={seaData} type={'Sea'} />}
          </CardContent>
        </Card>
      </div>
    );
  }

  renderTempLivingCard() {
    const {
      classes,
      tempLivingSummary,
    } = this.props;

    // if (ServiceStatuses.TempLivingServiceStatus === null) {
    //   return null;
    // }

    // For now, take the latest portion of temp living, in the future we may want to display all of them.
    const tempLivingInfo = (tempLivingSummary && tempLivingSummary.length > 0) ? tempLivingSummary.slice(-1)[0] : null;

    // Confirm the temp living info isn't empty
    if (!tempLivingInfo || (tempLivingInfo && Object.values(tempLivingInfo).every(x => x === null))) {
      return null;
    }

    return (
      <div className={classes.cardContainer}>
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.cardHeader}>
              <Paper className={classes.cardIcon} style={{ background: 'linear-gradient(45deg, #36939b, #34838a)' }}>
                <FontAwesomeIcon icon="bed" color="white" size="4x" />
              </Paper>
              <div className={classes.cardHeaderText}>TEMP LIVING</div>
            </div>
            {(tempLivingSummary.complexAddress1 || tempLivingSummary.complexState || tempLivingSummary.complexCity) &&
              <div>
                <Typography className={classes.subtitle}>{tempLivingSummary.complexAddress1}</Typography>
                <Typography className={classes.subtitle}>{tempLivingSummary.complexAddress2}</Typography>
                <Typography className={classes.subtitle}>{`${tempLivingSummary.complexCity  }, ${  tempLivingSummary.complexState  } ${  tempLivingSummary.complexZip}`}</Typography>
              </div>
            }
                <Fragment>
                  <Grid container>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="Est. Moved In Date"
                        name="EstMoveInDate"
                        textColor="#36939b"
                        value={(tempLivingSummary.estMoveInDate) ? formatDateWithSlashes(tempLivingSummary.estMoveInDate) : ' '}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="Actual Moved In Date"
                        name="actPurchDate"
                        textColor="#36939b"
                        value={(tempLivingSummary.actMoveInDate) ? formatDateWithSlashes(tempLivingSummary.actMoveInDate) : ' '}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="Est. Moved Out Date"
                        name="estPurchDate"
                        textColor="#36939b"
                        value={(tempLivingSummary.estMoveOutDate) ? formatDateWithSlashes(tempLivingSummary.estMoveOutDate) : ' '}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="Actual Moved Out Date"
                        name="actPurchDate"
                        textColor="#36939b"
                        value={(tempLivingSummary.actMoveOutDate) ? formatDateWithSlashes(tempLivingSummary.actMoveOutDate) : ' '}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="Days Allowed"
                        name="DaysAllowed"
                        textColor="#36939b"
                        value={(tempLivingSummary.daysAllowed) ? tempLivingSummary.daysAllowed : ' '}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="Days Used"
                        name="DaysUsed"
                        textColor="#36939b"
                        value={(tempLivingSummary.daysUsed) ? tempLivingSummary.daysUsed : ' '}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="# of Adults"
                        name="NAdults"
                        textColor="#36939b"
                        value={(tempLivingSummary.nAdults) ? tempLivingSummary.nAdults : ' '}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="# of Children"
                        name="NChildren"
                        textColor="#36939b"
                        value={(tempLivingSummary.nChildren) ? tempLivingSummary.nChildren : ' '}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="# of Bedrooms"
                        name="NBedrooms"
                        textColor="#36939b"
                        value={(tempLivingSummary.nBedrooms) ? tempLivingSummary.nBedrooms : ' '}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="# of Pets"
                        name="NPets"
                        textColor="#36939b"
                        value={(tempLivingSummary.nPets) ? tempLivingSummary.nPets : ' '}
                      />
                    </Grid>
                  </Grid>
                </Fragment>
            
          </CardContent>
        </Card>
      </div>
    );
  }

  renderDestinationCard() {
    const {
      services,
      classes,
      transfereeInfo,
      homesaleSummary
    } = this.props;
    if ((services.filter(s => s.authorizationFileServiceTypeId === '007E4298-676F-4E50-8791-FA6464CD5358') === null && services.filter(s => s.authorizationFileServiceTypeId === '1F0148DF-2898-4D52-88F1-03F8CEAFB74F') === null) || homesaleSummary === null) {
      return null;
    }
    return (
      <div className={classes.cardContainer}>
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.cardHeader}>
              <Paper className={classes.cardIcon} style={{ background: 'linear-gradient(45deg, #fd6120, #ef5f2f)' }}>
                <FontAwesomeIcon icon="location-arrow" color="white" size="4x" />
              </Paper>
              <div className={classes.cardHeaderText}>DESTINATION</div>
            </div>
            {(transfereeInfo.destinationAddressLine1 || transfereeInfo.destinationCity || transfereeInfo.destinationState) &&
              <div>
                <Typography className={classes.subtitle}>{transfereeInfo.destinationAddressLine1}</Typography>
                <Typography className={classes.subtitle}>{transfereeInfo.destinationAddressLine2}</Typography>
                <Typography className={classes.subtitle}>{`${transfereeInfo.destinationCity  }, ${  transfereeInfo.destinationState  } ${  transfereeInfo.destinationZip}`}</Typography>
              </div>
            }
            {
              services.filter(s => s.authorizationFileServiceTypeId === '007E4298-676F-4E50-8791-FA6464CD5358') !== null &&
                <Fragment>
                  <Grid container>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="Est. Purchase Date"
                        name="estPurchDate"
                        textColor="#fd6120"
                        value={(homesaleSummary.scheduleClose) ? formatDateWithSlashes(homesaleSummary.scheduleClose) : ' '}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <ReadOnlyText
                        label="Actual Purchase Date"
                        name="actPurchDate"
                        textColor="#fd6120"
                        value={(homesaleSummary.acloseDate) ? formatDateWithSlashes(homesaleSummary.acloseDate) : ' '}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={12}>
                      <ReadOnlyText
                        label="Purchase Price"
                        name="purchasePrice"
                        textColor="#fd6120"
                        value={(homesaleSummary.purchasePrice) ? homesaleSummary.purchasePrice : ' '}
                        inputComponent={CurrencyFormat}
                      />
                    </Grid>
                  </Grid>
                </Fragment>
            }
            
          </CardContent>
        </Card>
      </div>
    );
  }

  renderEmployeeJobCard() {
    const { classes, customFields, ptCodeHistories, isPtCodeProcessActive, transfereeInfo, clientInfo } = this.props;
    const normalizedPTCodeHistories = Array.isArray(ptCodeHistories) ? ptCodeHistories : [];

    const visibleCustomFields = Array.isArray(customFields)
      ? customFields.filter((field) => this.shouldDisplayCustomField(field, transfereeInfo, customFields, clientInfo?.clientDivisions, clientInfo?.clientRelocationPolicies))
      : [];

    //const filteredClientFields = clientFields.filter((field) => field.UserGroup !== 'AuthOnly');
    if (visibleCustomFields.length === 0 && (!isPtCodeProcessActive || normalizedPTCodeHistories.length === 0)) {
      return null;
    }

    const getPTCodeField = (ptCode, camelCaseName, pascalCaseName) => {
      return ptCode && (ptCode[camelCaseName] ?? ptCode[pascalCaseName]);
    };

    return (
      <div className={classes.cardContainer}>
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.cardHeader}>
              <Paper className={classes.cardIcon} style={{ background: 'linear-gradient(45deg, #67a783, #4d8766)' }}>
                <div style={{ marginLeft: '8px', marginTop: '5px' }}>
                  <FontAwesomeIcon icon="edit" color="white" size="4x" />
                </div>
              </Paper>
              <div className={classes.cardHeaderText}>EMPLOYEE JOB INFORMATION</div>
            </div>
            {visibleCustomFields.map((field, i) => {
              return (
                <Grid key={i} container>
                  <Grid key={i} item xs={12}>
                    <ReadOnlyText
                      key={i}
                      label={field.fieldLabel}
                      textColor="#67a783"
                      value={this.formatCustomFieldValue(field)}
                    />
                  </Grid>
                </Grid>
              );
            })
            }
            {isPtCodeProcessActive && normalizedPTCodeHistories.length > 0 &&
              <>
                <Typography className={classes.subtitle} style={{ marginTop: '0.5rem', fontWeight: 600 }}>PT Codes</Typography>
                {normalizedPTCodeHistories.map((ptCode, index) => {
                  const effectiveStartDate = getPTCodeField(ptCode, 'effectiveStartDate', 'EffectiveStartDate');
                  const effectiveEndDate = getPTCodeField(ptCode, 'effectiveEndDate', 'EffectiveEndDate');

                  return (
                    <Fragment key={`pt-code-history-${index}`}>
                      {index > 0 && <hr />}
                      <Grid container>
                        <Grid item xs={12}>
                          <ReadOnlyText
                            label="Project Code"
                            textColor="#67a783"
                            value={getPTCodeField(ptCode, 'projectCode', 'ProjectCode') || ' '}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <ReadOnlyText
                            label="Task Code"
                            textColor="#67a783"
                            value={getPTCodeField(ptCode, 'taskCode', 'TaskCode') || ' '}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <ReadOnlyText
                            label="Percentage"
                            textColor="#67a783"
                            value={getPTCodeField(ptCode, 'percentage', 'Percentage') ?? ' '}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <ReadOnlyText
                            label="Effective Start Date"
                            textColor="#67a783"
                            value={effectiveStartDate ? formatDateWithSlashes(effectiveStartDate) : ' '}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <ReadOnlyText
                            label="Effective End Date"
                            textColor="#67a783"
                            value={effectiveEndDate ? formatDateWithSlashes(effectiveEndDate) : ' '}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <ReadOnlyText
                            label="Notes"
                            textColor="#67a783"
                            value={getPTCodeField(ptCode, 'notes', 'Notes') || ' '}
                          />
                        </Grid>
                      </Grid>
                    </Fragment>
                  );
                })}
              </>
            }
          </CardContent>
        </Card>
      </div>
    );
  }

  renderInternationalCard() {
    const {
      classes,
      globalSummary,
      services
    } = this.props;

    if (services.filter(s => s.authorizationFileServiceTypeId === 'E5C3355E-9171-4968-A142-1A9D643A4B7D') === null || !globalSummary) {
      return null;
    }

    return (
      <div className={classes.internationalCardContainer}>
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.cardHeaderIntl}>
              <Paper className={classes.cardIcon} style={{ background: 'linear-gradient(45deg, #669999, #51929b)' }}>
                <div style={{ marginLeft: '8px', marginTop: '5px' }}>
                  <FontAwesomeIcon icon="plane" color="white" size="4x" />
                </div>
              </Paper>
              <div className={classes.cardHeaderText}>INTERNATIONAL INFORMATION</div>
            </div>

            <Grid container>
              <Grid item md={6}>
                <h4 className={classes.serviceSubHeader}>Assignment Overview</h4>

                <Grid container spacing={1}>
                  <Grid item xs ={10} sm={8} md={7}>
                    {INTERNATIONAL_FIELDS_OVERVIEW_ONE.map((row, i) => {
                      return (
                        <Grid container sx={{padding: '10px', margin: '15px'}} key={i}>
                          {row.map((field) => {
                            const { formatter, key, label, multiline, size } = field;
                            let value = globalSummary[key] ? globalSummary[key] : '-';
                            const columnSize = size ? size : 8;

                            if (typeof formatter === 'function' && value) {
                              value = formatter(value);
                            }

                            return (
                              <Grid item xs={columnSize} sm={columnSize} md={columnSize} lg={columnSize} key={key}>
                                <ReadOnlyText
                                  label={label}
                                  textColor="#669999"
                                  value={value}
                                  multiline={multiline}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>

                      );
                    })}
                  </Grid>
                  <Grid item md={5}>
                      {INTERNATIONAL_FIELDS_OVERVIEW_TWO.map((row, i) => {
                        return (
                          <Grid container key={i}>
                            {row.map((field) => {
                              const { formatter, key, label, multiline, size } = field;
                              let value = globalSummary[key] ? globalSummary[key] : '-';
                              const columnSize = size ? size : 10;

                              if (typeof formatter === 'function' && value) {
                                value = formatter(value);
                              }

                              return (
                                <Grid item xs={columnSize} key={key}>
                                  <ReadOnlyText
                                    label={label}
                                    textColor="#669999"
                                    value={value}
                                    multiline={multiline}
                                  />
                                </Grid>
                              );
                            })}
                          </Grid>

                        );
                      })}
                  </Grid>
                </Grid>
              </Grid>
              
              <Grid item md={3} style={{ borderLeft: '1px solid black', marginLeft: '2.5em'}}>
                  <h4 className={classes.serviceSubHeader}>Cost Estimates</h4>
                  {INTERNATIONAL_FIELDS_COSTS.map((row, i) => {
                    return (
                      <Grid container key={i}>
                        {row.map((field) => {
                          const { formatter, key, label, multiline, size } = field;
                          let value = globalSummary[key] ? globalSummary[key] : '-';
                          const columnSize = size ? size : 4;

                          if (typeof formatter === 'function' && value) {
                            value = formatter(value);
                          }

                          return (
                            <Grid item xs={columnSize} key={key}>
                              <ReadOnlyText
                                label={label}
                                textColor="#669999"
                                value={value}
                                multiline={multiline}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  })}
                  <hr/>
                  <h4 className={classes.serviceSubHeader}>Visa and Imigration</h4>
                  {INTERNATIONAL_FIELDS_VISA.map((row, i) => {
                    return (
                      <Grid container key={i}>
                        {row.map((field) => {
                          const { formatter, key, label, multiline, size } = field;
                          let value = globalSummary[key] ? globalSummary[key] : '-';
                          const columnSize = size ? size : 8;

                          if (typeof formatter === 'function' && value) {
                            value = formatter(value);
                          }

                          return (
                            <Grid item xs={columnSize} key={key}>
                              <ReadOnlyText
                                label={label}
                                textColor="#669999"
                                value={value}
                                multiline={multiline}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  })}
                </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
    );
  }

  render() {
    const { classes, clientDivisions } = this.props;
    return (
      <div className={classes.cardsContainer}>
        {this.renderHomeSaleCard()}
        {userHasPermission("feature:showEETempLivingCard", clientDivisions) && this.renderTempLivingCard()}
        {this.renderDestinationCard()}
        {this.renderHouseholdGoodsCard()}
        {this.renderEmployeeJobCard()}
        {this.renderInternationalCard()}
      </div>
    );
  }
}

ServicesCards.propTypes = {
  classes: PropTypes.object.isRequired,
  clientDivisions: PropTypes.array.isRequired,
};

ServicesCards = withStyles(styles)(ServicesCards);

export default ServicesCards;